import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { LogMessage } from "types";

import medallionsImage from "assets/enhancements.svg";
import reactionImage from "assets/message-reaction-icon.svg";
import { Tooltip, TooltipDesc, TooltipName } from "components/base/Tooltip";
import { media } from "components/styles/breakpoints";
import { useEffect, useRef, useState } from "react";
import {
  addLogMessage,
  reactToLogMessage,
  viewPlayerDetails,
} from "redux/actions";
import { getUserInfo } from "redux/selectors/characterSelectors";
import { getLogMessagesInfo } from "redux/selectors/gameSelectors";
import { COLORS } from "utils/constants";
import { BATTLE_RATINGS } from "data/battleRatings";

const LogMessagesWrapper = styled.div`
  border-radius: 4px;
  background-color: #00000080;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const LogMessages = styled.div`
  padding-top: 4px;
  overflow-y: scroll;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1px solid #43fffa40;
  border-bottom: none;
  border-radius: 4px 4px 0 0;

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.desktop`
    padding-top: 8px;
  `}
`;

// Reactions

const MessageReactionsWrapper = styled.div`
  height: 12px;

  ${media.desktop`
    height: 16px;
  `}
`;

const MessageReactions = styled.div<{ hasReactions: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2da5b2;
  height: 12px;
  cursor: pointer;
  transition: all 200ms ease-in;
  font-size: 12px;

  ${media.desktop`
    font-size: 14px;
    height: 16px;
  `}

  opacity: ${({ hasReactions }) => (hasReactions ? 1 : 0)};

  &:hover {
    opacity: 1;
    filter: saturate(1.5) brightness(1.5);
  }
`;

const ReactionIcon = styled.img`
  height: 12px;

  ${media.desktop`
    height: 16px;
  `}
`;

// Message

const LogMessageWrapper = styled.div`
  display: flex;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
  padding: 2px 6px;

  :hover {
    background-color: #ffffff10;
    ${MessageReactions} {
      opacity: 1;
    }
  }

  ${media.desktop`
    padding: 3px 10px;
  `}
`;

const MessageMain = styled.div`
  display: flex;
`;

const MessageTime = styled.span`
  color: #5a6b7b;
  font-size: 10px;
  margin-right: 6px;
  margin-top: 3px;
`;

const MessageContent = styled.span`
  line-height: 1.6;
  vertical-align: middle;

  color: #8a9aab;
  font-size: 12px;

  ${media.desktop`
    font-size: 14px;
  `}
`;

const MessageUser = styled.span`
  color: #2da5b2;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;

  background-color: #2da5b240;
  padding: 0 4px;
  border-radius: 4px;

  ${media.desktop`
    font-size: 14px;
  `}
`;

const MessageMob = styled(MessageUser)`
  color: ${COLORS.MOBS};
  background-color: ${COLORS.MOBS}40;
  cursor: default;
`;

const MedallionsInfo = styled.span`
  font-size: 10px;
  margin-left: 4px;

  ${media.desktop`
    font-size: 12px;
  `};
`;

const MedallionsIcon = styled.span<{ image: string }>`
  height: 10px;
  width: 10px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: #2da5b2;
  display: inline-block;
  margin-bottom: -1px;
`;

const MobMedallionsIcon = styled(MedallionsIcon)`
  background-color: ${COLORS.MOBS};
`;

const MedallionsValue = styled.span`
  margin-left: 1px;
`;

const PlatinumWrapper = styled.span`
  color: ${COLORS.RATINGS.PLATINUM};
  white-space: nowrap;
`;

const PlatinumIcon = styled.img`
  height: 14px;
  margin-bottom: -2px;
  margin-right: 2px;
`;

const ChatMessageText = styled.span`
  margin-left: 4px;
  color: white;
`;

// Padding top of the chat messages minus gap between messages
const ChatLatest = styled.div`
  min-height: 4px;

  ${media.desktop`
    min-height: 8px;
  `}
`;

const ChatInput = styled.input`
  display: block;
  border: none;
  border: 1px solid #43fffa40;
  background-color: transparent;
  font-family: "Jost", sans-serif;
  color: white;
  text-align: left;
  font-size: 12px;
  padding: 4px 6px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 0 0 4px 4px;

  &:focus {
    outline: none;
    background-color: #ffffff05;
    border-color: #43fffa50;
  }

  &::placeholder {
    color: #ffffff40;
  }

  ${media.desktop`
    font-size: 14px;
    padding: 5px 10px;
  `}
`;

const MessageUserInfo = ({ message }: { message: LogMessage }) => {
  const dispatch = useDispatch();

  return (
    <MessageUser onClick={() => dispatch(viewPlayerDetails(message.userId))}>
      {message.userName}
      {typeof message.medallions === "number" && (
        <MedallionsInfo>
          <MedallionsIcon image={medallionsImage} />
          <MedallionsValue>{message.medallions}</MedallionsValue>
        </MedallionsInfo>
      )}
    </MessageUser>
  );
};

const MobInfo = ({ message }: { message: LogMessage }) => {
  if (!message.mobName) return null;

  return (
    <MessageMob>
      {message.mobName}
      <MedallionsInfo>
        <MobMedallionsIcon image={medallionsImage} />
        <MedallionsValue>{message.mobMedallions}</MedallionsValue>
      </MedallionsInfo>
    </MessageMob>
  );
};

const WelcomeMessageContent = ({ message }: { message: LogMessage }) => {
  return (
    <>
      Welcome <MessageUserInfo message={message} /> to Bishop City!
    </>
  );
};

const PlatinumMessageContent = ({ message }: { message: LogMessage }) => {
  return (
    <>
      <MessageUserInfo message={message} /> decimated{" "}
      <MobInfo message={message} /> for a{" "}
      <PlatinumWrapper>
        <PlatinumIcon src={BATTLE_RATINGS.platinum.image} />
        {BATTLE_RATINGS.platinum.name}
      </PlatinumWrapper>{" "}
      rating!
    </>
  );
};

const ChatMessageContent = ({ message }: { message: LogMessage }) => {
  return (
    <>
      <MessageUserInfo message={message} />
      <ChatMessageText>{message.text}</ChatMessageText>
    </>
  );
};

const AchievementMessageContent = ({ message }: { message: LogMessage }) => {
  return <>{message.text}</>;
};

export default function Chat() {
  const dispatch = useDispatch();

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  const { userName } = useSelector(getUserInfo);
  const { logMessages, mutedUsers } = useSelector(getLogMessagesInfo);

  const [messageText, setMessageText] = useState("");

  const isChatDisabled = userName ? mutedUsers.includes(userName) : false;
  const messages = logMessages.filter(
    (message) =>
      !(message.type === "message" && mutedUsers.includes(message.userName))
  ); // don't mute achievements

  useEffect(() => {
    const lastMessage = logMessages[logMessages.length - 1];
    if (!lastMessage) return;

    // Don't scroll to bottom if the updated data is just a new reaction
    if (lastMessage.id === lastMessageIdRef.current) return;

    lastMessageIdRef.current = lastMessage.id;
    if (chatRef.current) {
      chatRef.current.scrollIntoView({
        behavior: "auto",
        block: "end",
      });
    }
  }, [chatRef, logMessages]);

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessageText(event.target.value);
  };

  const handleMessageKeydown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" && messageText.trim() !== "") {
      dispatch(addLogMessage({ type: "message", text: messageText }));
      setMessageText("");
    }
  };

  const handleClickReactions = (message: LogMessage) => {
    dispatch(reactToLogMessage(message));
  };

  return (
    <LogMessagesWrapper>
      <LogMessages>
        {messages.map((message: LogMessage) => {
          const datetime = new Date(message.time);
          const messageDate = datetime
            .toLocaleDateString([], {
              day: "numeric",
              month: "numeric",
            })
            .replace(/\//g, ".");
          const messageTime = datetime.toLocaleTimeString([], {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <LogMessageWrapper key={message.id}>
              <MessageMain>
                <MessageTime>
                  [{messageDate}⋄{messageTime}]
                </MessageTime>
                <MessageContent>
                  {message.type === "welcome" && (
                    <WelcomeMessageContent message={message} />
                  )}
                  {message.type === "platinum" && (
                    <PlatinumMessageContent message={message} />
                  )}
                  {message.type === "message" && (
                    <ChatMessageContent message={message} />
                  )}
                  {message.type === "achievement" && (
                    <AchievementMessageContent message={message} />
                  )}
                </MessageContent>
              </MessageMain>
              <MessageReactionsWrapper>
                <Tooltip
                  content={
                    <>
                      <TooltipName>Click to React</TooltipName>
                      {message.reactions.length > 0 && (
                        <TooltipDesc>
                          Reactions from{" "}
                          {message.reactions
                            .map((reaction) => reaction.senderName)
                            .join(", ")}
                        </TooltipDesc>
                      )}
                    </>
                  }
                >
                  <MessageReactions
                    hasReactions={!!message.reactions.length}
                    onClick={() => handleClickReactions(message)}
                  >
                    {!!message.reactions.length && message.reactions.length}
                    <ReactionIcon src={reactionImage} />
                  </MessageReactions>
                </Tooltip>
              </MessageReactionsWrapper>
            </LogMessageWrapper>
          );
        })}
        <ChatLatest ref={chatRef} />
      </LogMessages>
      <ChatInput
        ref={inputRef}
        placeholder="Enter a message"
        value={messageText}
        onChange={handleMessageChange}
        onKeyDown={handleMessageKeydown}
        maxLength={300}
        disabled={isChatDisabled}
      />
    </LogMessagesWrapper>
  );
}
